import type { CSSProperties } from 'react'
import { C } from '../theme'
import type { CalendarEvent } from '../types'
import { AUDIENCE_BADGE } from '../data/events'
import { dayNumber, longDate, monthShort, monthYear, pad2, weekdayShort } from '../lib/format'
import { useCountdown } from '../hooks/useCountdown'
import { Reveal, RevealStagger } from './Reveal'
import { WhatsAppIcon } from './icons'
import { useContent } from '../content/ContentContext'

const cdBox: CSSProperties = {
  background: 'rgba(255,255,255,.08)', border: '1px solid rgba(255,255,255,.14)',
  borderRadius: 12, padding: '16px 4px', textAlign: 'center',
}
const cdNum: CSSProperties = { fontFamily: "'Spectral',serif", fontSize: 32, lineHeight: 1, color: '#fff' }
const cdLbl: CSSProperties = { fontSize: 10, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: '#9fb4d6', marginTop: 7 }

export function Calendar({ events }: { events: CalendarEvent[] }) {
  const { home, settings } = useContent()
  const cal = home.calendar ?? {}
  const whatsappGroup = settings.social?.whatsappGroup
  const cd = useCountdown(events)
  const eyebrow = events.length ? `Calendario · ${monthYear(events[0].date)}` : 'Calendario'

  return (
    <section id="calendario" aria-label="Calendario de próximos encuentros" className="sec" style={{ padding: '96px 40px 104px', background: C.sand }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <Reveal style={{ textAlign: 'center', maxWidth: 680, margin: '0 auto 44px' }}>
          <div style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: C.red, marginBottom: 18 }}>{eyebrow}</div>
          <h2 className="section-title" style={{ fontFamily: "'Spectral',serif", fontWeight: 400, fontSize: 46, lineHeight: 1.08, margin: '0 0 8px', color: C.ink, letterSpacing: '-.01em' }}>
            {cal.title}
          </h2>
          <p style={{ fontFamily: "'Spectral',serif", fontStyle: 'italic', fontSize: 17, color: C.muted, margin: 0 }}>{cal.subtitle}</p>
        </Reveal>

        {/* Featured next event + countdown — only when there is one to show. */}
        {cd.next && (
        <Reveal
          className="stack"
          style={{ position: 'relative', overflow: 'hidden', background: C.navy, borderRadius: 18, padding: '38px 42px', color: '#fff', display: 'grid', gridTemplateColumns: '1.25fr 1fr', gap: 30, alignItems: 'center', boxShadow: '0 24px 60px rgba(21,35,63,.22)' }}
        >
          <div style={{ position: 'absolute', right: -70, top: -70, width: 260, height: 260, borderRadius: '50%', background: `radial-gradient(circle,${C.blue},transparent 68%)`, opacity: 0.6, pointerEvents: 'none' }} />
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: C.red, animation: 'cddPulse 1.8s infinite' }} />
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.18em', textTransform: 'uppercase', color: C.goldSoft }}>{cal.nextEventLabel}</span>
            </div>
            <h3 style={{ fontFamily: "'Spectral',serif", fontWeight: 500, fontSize: 34, margin: '0 0 8px', color: '#fff' }}>{cd.next?.title}</h3>
            <div style={{ fontSize: 16, color: '#cdd9ef', marginBottom: 4 }}>{cd.next ? `${longDate(cd.next.date)} · ${cd.next.time}` : ''}</div>
            <div style={{ fontSize: 14.5, color: '#9fb4d6', marginBottom: 24 }}>{cd.next?.location}, Puerto Rico</div>
            <a className="btn" href="#contacto" style={{ display: 'inline-block', background: C.red, color: '#fff', fontWeight: 700, fontSize: 14.5, padding: '14px 28px', borderRadius: 3, textDecoration: 'none', boxShadow: '0 8px 22px rgba(193,39,45,.4)' }}>
              {cal.attendCtaLabel}
            </a>
          </div>
          <div style={{ position: 'relative', display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
            {[
              { n: cd.days, l: 'Días' },
              { n: cd.hours, l: 'Horas' },
              { n: cd.mins, l: 'Min' },
              { n: cd.secs, l: 'Seg' },
            ].map((b) => (
              <div key={b.l} style={cdBox}>
                <div style={cdNum}>{pad2(b.n)}</div>
                <div style={cdLbl}>{b.l}</div>
              </div>
            ))}
          </div>
        </Reveal>
        )}

        {/* No events published in the CMS yet. */}
        {events.length === 0 && (
          <Reveal style={{ textAlign: 'center', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 14, padding: '48px 32px', boxShadow: '0 10px 26px rgba(21,35,63,.06)' }}>
            <p style={{ fontFamily: "'Spectral',serif", fontSize: 21, color: C.ink, margin: '0 0 8px' }}>
              {cal.emptyTitle}
            </p>
            <p style={{ fontSize: 15, color: C.muted, margin: 0 }}>
              {cal.emptyText}
            </p>
          </Reveal>
        )}

        {/* Event list */}
        <RevealStagger style={{ display: 'flex', flexDirection: 'column', gap: 13, marginTop: 24 }}>
          {events.map((e) => {
            const badge = AUDIENCE_BADGE[e.audience]
            return (
              <div key={e.id} className="cal-row" style={{ display: 'flex', gap: 20, alignItems: 'center', background: '#fff', border: `1px solid ${C.line}`, borderRadius: 14, padding: '16px 20px', boxShadow: '0 10px 26px rgba(21,35,63,.06)' }}>
                <div style={{ flex: '0 0 auto', width: 66, textAlign: 'center', background: C.sand2, borderRadius: 11, padding: '10px 0' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: C.red }}>{weekdayShort(e.date)}</div>
                  <div style={{ fontFamily: "'Spectral',serif", fontSize: 28, lineHeight: 1, color: C.ink }}>{dayNumber(e.date)}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.1em', color: C.muted }}>{monthShort(e.date)}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: badge.fg, background: badge.bg, padding: '3px 10px', borderRadius: 30, marginBottom: 8 }}>
                    {badge.label}
                  </span>
                  <h4 style={{ fontFamily: "'Spectral',serif", fontWeight: 500, fontSize: 21, margin: '0 0 3px', color: C.ink }}>{e.title}</h4>
                  <p style={{ fontSize: 14, color: C.muted2, margin: 0 }}>{e.description}</p>
                </div>
                <div style={{ flex: '0 0 auto', textAlign: 'right' }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.blue }}>{e.time}</div>
                  <div style={{ fontSize: 12.5, color: C.muted }}>{e.location}</div>
                </div>
              </div>
            )
          })}
        </RevealStagger>

        {whatsappGroup && cal.whatsappCtaLabel && (
          <Reveal style={{ textAlign: 'center', marginTop: 40 }}>
            <a className="btn" href={whatsappGroup} target="_blank" rel="noopener" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 15, padding: '15px 30px', borderRadius: 40, textDecoration: 'none', boxShadow: '0 10px 26px rgba(37,211,102,.3)' }}>
              <WhatsAppIcon size={19} /> {cal.whatsappCtaLabel}
            </a>
          </Reveal>
        )}
      </div>
    </section>
  )
}
